import {
  Bot,
  CloudCog,
  Code2,
  Fingerprint,
  GitBranch,
  Network,
  ShieldCheck,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

export const focusAreas = [
  'API Platforms',
  'Cloud Native Architecture',
  'Enterprise Web Applications',
  'Platform Engineering',
  'Azure / Kubernetes / DevSecOps',
  'Identity & Access Management',
  'System Modernisation',
  'Enterprise Integration',
  'AI-ready API ecosystems',
];

export const heroProof = [
  '17+ years enterprise delivery',
  'Public sector, finance, retail, regulatory, and startup environments',
  'Hands-on architecture through to engineering delivery',
];

export const trustedOrganisations = [
  'Ofgem',
  'Transport for London',
  'Deutsche Bank',
  'Homes England',
  'DEFRA',
  'Next Retail',
];

export const outcomes = [
  'Business platform transformation',
  'Enterprise scalability',
  'Technical leadership',
  'Delivery acceleration',
  'Platform modernisation',
  'API-first business architecture',
  'Cloud operational maturity',
  'Engineering leadership',
];

export const services: Array<{
  title: string;
  summary: string;
  capabilities: string[];
  outcomes: string[];
  icon: LucideIcon;
}> = [
  {
    title: 'API Platform Engineering',
    summary: 'Enterprise-grade API ecosystems designed for scale, governance, partner integration, and AI readiness.',
    capabilities: [
      'Azure API Management',
      'REST APIs',
      'Microservices',
      'Event-driven architecture',
      'API governance',
      'API security',
      'API lifecycle management',
    ],
    outcomes: [
      'Faster integrations',
      'Scalable partner ecosystems',
      'Reduced operational complexity',
      'AI-ready platforms',
      'Improved security and governance',
    ],
    icon: Network,
  },
  {
    title: 'Cloud Native Engineering',
    summary: 'Resilient platform engineering across Azure, Kubernetes, automation, and secure delivery pipelines.',
    capabilities: ['Azure', 'AWS', 'GCP', 'Kubernetes', 'Docker', 'AKS', 'Terraform', 'CI/CD', 'GitOps', 'DevSecOps'],
    outcomes: ['Modern cloud foundations', 'Reliable deployments', 'Operational maturity', 'Secure delivery at speed'],
    icon: CloudCog,
  },
  {
    title: 'Enterprise Web Applications',
    summary: 'Modern application engineering for scalable business platforms and distributed enterprise systems.',
    capabilities: ['.NET', 'Angular', 'React', 'TypeScript', 'Distributed systems', 'Event-driven platforms', 'Enterprise integration'],
    outcomes: ['Maintainable product platforms', 'Better user journeys', 'Scalable system boundaries', 'Higher delivery confidence'],
    icon: Code2,
  },
  {
    title: 'Identity & Security Engineering',
    summary: 'Enterprise IAM patterns for secure customer, workforce, and platform access across regulated environments.',
    capabilities: ['Microsoft Entra ID', 'Azure AD B2C', 'OAuth2', 'OpenID Connect', 'SAML', 'MFA', 'RBAC', 'Zero Trust architecture'],
    outcomes: ['Stronger access control', 'Reduced identity risk', 'Better governance', 'Secure customer journeys'],
    icon: Fingerprint,
  },
  {
    title: 'Platform Modernisation',
    summary: 'Turning legacy systems into cloud-native platforms with clearer boundaries, automation, and delivery practices.',
    capabilities: [
      'Legacy system modernisation',
      'Monolith to microservices migration',
      'Cloud-native architecture',
      'Delivery pipeline improvement',
      'DevSecOps adoption',
    ],
    outcomes: ['Lower change friction', 'Improved resilience', 'Reduced technical debt', 'Faster release cycles'],
    icon: GitBranch,
  },
  {
    title: 'AI-Ready API Platforms',
    summary: 'Preparing platforms for AI integration through secure APIs, structured data flows, and scalable architecture.',
    capabilities: [
      'API ecosystem design',
      'Secure integration patterns',
      'Structured data pipelines',
      'MCP and AI integration readiness',
      'Cloud-native platform foundations',
    ],
    outcomes: ['Safer AI adoption', 'Reusable data access', 'Clear integration controls', 'Future-ready platform architecture'],
    icon: Bot,
  },
];

export const differentiators = [
  {
    title: 'Enterprise + hands-on',
    text: 'Architecture, technical strategy, and delivery leadership backed by current engineering depth.',
    icon: Workflow,
  },
  {
    title: 'Regulated-sector experience',
    text: 'Public sector and finance delivery experience signals compliance, security, governance, and enterprise maturity.',
    icon: ShieldCheck,
  },
  {
    title: 'API and platform focus',
    text: 'A commercially relevant specialism for organisations modernising services, integration models, and digital channels.',
    icon: Network,
  },
];

export const industries = [
  'Government digital transformation',
  'Financial services platforms',
  'Retail and ecommerce systems',
  'Regulatory technology',
  'High-growth startup platforms',
  'Enterprise integration programmes',
];

export const leadTypes = [
  'Enterprise consulting leads',
  'API platform transformation projects',
  'Cloud migration engagements',
  'Technical architecture contracts',
  'Fractional platform architect opportunities',
  'Government digital transformation contracts',
  'Startup CTO advisory opportunities',
];

export const blogTopics = [
  'API-First Architecture for Enterprise Platforms',
  'Azure API Management Best Practices',
  'Migrating Monoliths to Microservices',
  'Designing AI-Ready API Platforms',
  'Azure AD B2C Enterprise Identity Patterns',
  'Kubernetes for Enterprise Applications',
  'DevSecOps in Regulated Industries',
  'Event-Driven Architecture in Enterprise Systems',
  'Building Scalable Government Digital Services',
];

export const caseStudies = [
  {
    title: 'API Modernisation Review',
    sector: 'Enterprise integration',
    text: 'Assess API estates, governance, security, and integration readiness to identify practical modernisation priorities.',
    image: '/images/svk-project-review.png',
  },
  {
    title: 'Cloud Architecture Assessment',
    sector: 'Azure and Kubernetes platforms',
    text: 'Review platform maturity, DevSecOps pipelines, deployment models, and operating practices for scalable cloud delivery.',
    image: '/images/svk-operations-dashboard.png',
  },
  {
    title: 'Enterprise Platform Delivery',
    sector: 'Government, finance, retail, startups',
    text: 'Lead architecture and hands-on engineering for modern web applications, APIs, identity systems, and integrations.',
    image: '/images/svk-office-hero.png',
  },
];
